import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, ShieldCheck, Mail, Loader2, Trash2, Users, Calculator, Save } from "lucide-react";

export default function AdminUsers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ users: [], invites: [] });
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("advogado");
  const [inviting, setInviting] = useState(false);
  const [settings, setSettings] = useState({ salario_minimo: "", grau_insalubridade: "medio" });
  const [savingSettings, setSavingSettings] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/admin/users");
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403) { toast.error("Acesso restrito ao admin"); navigate("/dashboard"); }
      else toast.error("Falha ao carregar usuários");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/settings", {
        salario_minimo: parseFloat(settings.salario_minimo) || 0,
        grau_insalubridade: settings.grau_insalubridade,
      });
      toast.success("Parâmetros de cálculo salvos");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar parâmetros");
    } finally {
      setSavingSettings(false);
    }
  };

  const invite = async () => {
    if (!email.includes("@")) { toast.error("Informe um e-mail válido"); return; }
    setInviting(true);
    try {
      await api.post("/admin/invite", { email: email.trim().toLowerCase(), role });
      toast.success("Convite registrado — o acesso é liberado no 1º login com Google");
      setEmail("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao convidar");
    } finally { setInviting(false); }
  };

  const changeRole = async (u, newRole) => {
    try {
      await api.put(`/admin/users/${u.user_id}/role`, { role: newRole });
      toast.success("Papel atualizado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao atualizar papel");
    }
  };

  const removeInvite = async (em) => {
    try {
      await api.delete(`/admin/invite/${encodeURIComponent(em)}`);
      toast.success("Convite removido");
      load();
    } catch (e) { toast.error("Falha ao remover"); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="admin-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-600 mb-1">Administração</p>
            <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
              <Users className="h-7 w-7" /> Gestão de Usuários
            </h1>
          </div>
        </div>

        {/* Settings card */}
        <div className="bg-white border rounded-xl p-6 mb-8" data-testid="settings-card">
          <h2 className="font-display font-bold text-base mb-1 flex items-center gap-2"><Calculator className="h-4 w-4 text-amber-600" /> Parâmetros de cálculo</h2>
          <p className="text-sm text-muted-foreground mb-4">Usados na liquidação automática dos pedidos (base de insalubridade e grau padrão).</p>
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1 w-full">
              <label className="text-sm font-medium mb-1.5 block">Salário mínimo vigente (R$)</label>
              <Input data-testid="settings-salario-minimo" type="number" step="0.01" value={settings.salario_minimo}
                onChange={(e) => setSettings((s) => ({ ...s, salario_minimo: e.target.value }))} className="bg-white" />
            </div>
            <div className="flex-1 w-full">
              <label className="text-sm font-medium mb-1.5 block">Grau de insalubridade padrão</label>
              <Select value={settings.grau_insalubridade} onValueChange={(v) => setSettings((s) => ({ ...s, grau_insalubridade: v }))}>
                <SelectTrigger data-testid="settings-grau" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="minimo">Mínimo (10%)</SelectItem>
                  <SelectItem value="medio">Médio (20%)</SelectItem>
                  <SelectItem value="maximo">Máximo (40%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="save-settings-btn" onClick={saveSettings} disabled={savingSettings}
              className="bg-slate-900 hover:bg-slate-800 text-white gap-2">
              {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar
            </Button>
          </div>
        </div>

        {/* Invite card */}
        <div className="bg-white border rounded-xl p-6 mb-8">
          <h2 className="font-display font-bold text-base mb-1 flex items-center gap-2"><UserPlus className="h-4 w-4 text-amber-600" /> Convidar advogado</h2>
          <p className="text-sm text-muted-foreground mb-4">O usuário entra com a conta Google. Ao primeiro login, recebe o papel definido aqui.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input data-testid="invite-email-input" placeholder="email@escritorio.com" value={email}
                onChange={(e) => setEmail(e.target.value)} className="bg-white" type="email" />
            </div>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="invite-role-select" className="w-full sm:w-44 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="advogado">Advogado</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="invite-submit-btn" onClick={invite} disabled={inviting}
              className="bg-slate-900 hover:bg-slate-800 text-white gap-2">
              {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Convidar
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="h-40 rounded-xl bg-muted animate-pulse" />
        ) : (
          <>
            {data.invites.length > 0 && (
              <div className="mb-8">
                <h3 className="font-display font-bold text-sm mb-3">Convites pendentes ({data.invites.length})</h3>
                <div className="space-y-2">
                  {data.invites.map((i) => (
                    <div key={i.email} data-testid={`invite-${i.email}`} className="flex items-center justify-between bg-white border rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-amber-50 flex items-center justify-center"><Mail className="h-4 w-4 text-amber-600" /></div>
                        <div>
                          <p className="text-sm font-medium">{i.email}</p>
                          <Badge variant="secondary" className="text-[10px] capitalize mt-0.5">{i.role}</Badge>
                        </div>
                      </div>
                      <button onClick={() => removeInvite(i.email)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h3 className="font-display font-bold text-sm mb-3">Usuários ativos ({data.users.length})</h3>
            <div className="space-y-2" data-testid="users-list">
              {data.users.map((u) => (
                <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="flex items-center justify-between bg-white border rounded-lg p-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={u.picture} />
                      <AvatarFallback className="bg-amber-600 text-white text-sm">{(u.name || u.email || "U").charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate flex items-center gap-2">
                        {u.name || u.email}
                        {u.role === "admin" && <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                  </div>
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)}
                    disabled={u.email === user?.email}>
                    <SelectTrigger data-testid={`role-select-${u.user_id}`} className="w-36 bg-white h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="advogado">Advogado</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
